import { useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseUseCaseErrors } from '@webapp/modules/expense/business-logic/use-cases/ExpenseUseCaseErrors'
import messages from './messages'
import { useMutateApproveExpense } from '../../models/useMutateApproveExpense'

interface UseApproveExpensePresenterParams {
  expenseId: number
  selectedNextApproverId: Nullable<number>
  onSuccess: () => void
}

export const useApproveExpensePresenter = ({
  expenseId,
  selectedNextApproverId,
  onSuccess,
}: UseApproveExpensePresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutate, isLoading } = useMutateApproveExpense()
  const [isOverBudgetConfirmOpen, setIsOverBudgetConfirmOpen] = useState(false)

  const approve = async (shouldSkipBudgetCheck: boolean = false) => {
    try {
      await mutate({
        expenseId,
        nextApproverId: selectedNextApproverId,
        shouldSkipBudgetCheck,
      })

      toast.success({
        title: intl.formatMessage(messages.approveSuccess, { expenseId }),
      })

      window.analytics?.track('Document Approved', { type: 'expense' })

      onSuccess()
    } catch (err) {
      if (
        err instanceof ExpenseUseCaseErrors &&
        err.name === 'EXPENSE_APPROVE_OVER_BUDGET_CONFIRMATION_REQUIRED'
      ) {
        setIsOverBudgetConfirmOpen(true)
        return
      }

      const errorMessage = getMessageFromError(err)

      toast.error({
        title: intl.formatMessage(messages.approveError),
        children: errorMessage,
      })
    }
  }

  const confirmOverBudget = async () => {
    setIsOverBudgetConfirmOpen(false)
    await approve(true)
  }

  const cancelOverBudget = () => {
    setIsOverBudgetConfirmOpen(false)
  }

  return {
    commands: {
      approve,
      confirmOverBudget,
      cancelOverBudget,
    },
    states: {
      isApproving: isLoading,
      isOverBudgetConfirmOpen,
    },
  }
}
